import {
  browserProfilingIntegration,
  browserSessionIntegration,
  browserTracingIntegration,
  consoleLoggingIntegration,
  feedbackIntegration,
  init,
} from '@sentry/react';

import { readRuntimeEnv, readRuntimeNumber } from '@/config/runtimeEnv';
import { buildSentryOptions, scrubSentryEvent } from './sentry-options';

export function initSentry(): void {
  if (import.meta.env.DEV) return;

  // Read through readRuntimeEnv so a prod container (which serves a retagged
  // staging image with staging VITE_* baked in) overrides them with the
  // per-environment runtime-config.js generated at container start.
  const dsn = readRuntimeEnv('VITE_SENTRY_DSN');
  if (!dsn) return;

  const environment =
    readRuntimeEnv('VITE_SENTRY_ENVIRONMENT') ??
    (import.meta.env.MODE as string) ??
    'development';
  const sentryEnvironment = environment.trim().toLowerCase();
  if (sentryEnvironment !== 'staging' && sentryEnvironment !== 'production') {
    return;
  }
  const isProd = sentryEnvironment === 'production';
  const sampleRates = {
    tracesSampleRate: readRuntimeNumber(
      'VITE_SENTRY_TRACES_SAMPLE_RATE',
      isProd ? 0.2 : 1.0,
    ),
    profilesSampleRate: readRuntimeNumber(
      'VITE_SENTRY_PROFILES_SAMPLE_RATE',
      isProd ? 0.2 : 1.0,
    ),
  };

  init({
    dsn,
    environment: sentryEnvironment,
    release: readRuntimeEnv('VITE_SENTRY_RELEASE'),
    enableLogs: true,
    sendDefaultPii: false,
    ...buildSentryOptions(
      sentryEnvironment,
      {
        browserSessionIntegration,
        browserTracingIntegration,
        browserProfilingIntegration,
        feedbackIntegration,
        consoleLoggingIntegration,
      },
      sampleRates,
    ),
    initialScope: {
      tags: { service: 'admin-panel' },
    },
    beforeSend: scrubSentryEvent,
  });
}
